import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { OrderService } from '@proxy/orders';
import { OrderDto } from '@proxy/orders/models';
import { OrderStatus } from '@proxy/enums/order-status.enum';
import { canConfirmPayment } from '../order-payment';
import { LocalizationService } from '@abp/ng.core';
import { ConfirmationModalService } from '../../shared/services/confirmation-modal.service';
import { CurrencyPipe, DatePipe } from '@angular/common';
import { finalize } from 'rxjs';
// MetroButtonComponent is available through SharedModule

@Component({
  selector: 'app-order-detail',
  templateUrl: './order-detail.component.html',
  standalone: false
})
export class OrderDetailComponent implements OnInit {
  order: OrderDto | null = null;
  // winepassport#389 — confirmación de pago en curso: el botón queda deshabilitado.
  isConfirmingPayment = false;
  orderId: number | null = null;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private orderService: OrderService,
    private confirmation: ConfirmationModalService,
    private localization: LocalizationService
  ) {}

  formatCustomerAddress(order: OrderDto): string {
    return [order.customerAddress, order.customerProvince].filter(p => !!p).join(', ');
  }

  ngOnInit(): void {
    this.route.paramMap.subscribe(params => {
      const id = params.get('orderId');
      if (id) {
        this.orderId = +id;
        this.loadOrder();
      }
    });
  }

  loadOrder(): void {
    if (this.orderId) {
      this.orderService.get(this.orderId).subscribe({
        next: (order) => {
          this.order = order;
        },
        error: (error) => {
          console.error('Error loading order:', error);
          // TODO: Handle error appropriately, maybe redirect or show error message
        }
      });
    }
  }

  goBack(): void {
    this.router.navigate(['/backoffice/orders']);
  }

  getStatusText(status: OrderStatus | undefined): string {
    if (status === undefined) return '::Unknown';

    switch (status) {
      case OrderStatus.Pending:
        return '::OrderPending';
      case OrderStatus.Paid:
        return '::Paid';
      case OrderStatus.Shipped:
        return '::Shipped';
      case OrderStatus.Delivered:
        return '::Delivered';
      case OrderStatus.Cancelled:
        return '::Cancelled';
      case OrderStatus.PendingPayment:
        return '::OrderPendingPayment';
      default:
        return '::Unknown';
    }
  }

  getStatusClass(status: OrderStatus | undefined): string {
    if (status === undefined) return 'text-gray-500';

    switch (status) {
      case OrderStatus.Pending:
        return 'text-yellow-500';
      case OrderStatus.Paid:
        return 'text-blue-500';
      case OrderStatus.Shipped:
        return 'text-purple-500';
      case OrderStatus.Delivered:
        return 'text-green-500';
      case OrderStatus.Cancelled:
        return 'text-red-500';
      default:
        return 'text-gray-500';
    }
  }

  getPaymentMethodText(paymentMethod: string | undefined): string {
    if (!paymentMethod) return 'N/A';

    switch (paymentMethod.toLowerCase()) {
      case 'creditcard':
        return 'Credit Card';
      case 'bank':
        return 'Bank Transfer';
      default:
        return paymentMethod;
    }
  }

  showConfirmPaymentButton(): boolean {
    // winepassport#389 — misma regla que el listado de órdenes.
    return canConfirmPayment(this.order);
  }

  showDeliverButton(): boolean {
    if (!this.order) return false;

    // Show the button if the status is paid
    return this.order.orderStatus === OrderStatus.Paid;
  }

  showCancelOrderButton(): boolean {
    if (!this.order) return false;

    // Don't show the button if the order is already cancelled
    if (this.order.orderStatus === OrderStatus.Cancelled) return false;

    // Show the button for bank (transfer) or credit_card/card (Mercado Pago pending)
    const paymentMethod = this.order.paymentMethod?.toLowerCase();
    return paymentMethod === 'bank' || paymentMethod === 'credit_card' || paymentMethod === 'card';
  }

  isCancelOrderEnabled(): boolean {
    if (!this.order) return false;

    // Enable the button only if status is pending
    return this.order.orderStatus === OrderStatus.Pending;
  }

  // winepassport#389 — los tres confirms usan el modal reutilizable (ConfirmationModalService,
  // max-w-md) en vez de app-metro-modal, que en escritorio ocupaba casi todo el ancho.
  private askConfirmation(titleKey: string, messageKey: string, confirmKey: string, onConfirmed: () => void): void {
    this.confirmation
      .confirm({
        title: titleKey,
        message: messageKey,
        type: 'warning',
        confirmText: this.localization.instant(confirmKey),
        cancelText: this.localization.instant('::Cancel'),
        showCancelButton: true,
      })
      .subscribe(confirmed => {
        if (confirmed) onConfirmed();
      });
  }

  confirmPayment(): void {
    if (!this.order) return;
    this.askConfirmation('::ConfirmPayment', '::ConfirmPaymentMessage', '::ConfirmPayment', () => this.onConfirmPayment());
  }

  deliverOrder(): void {
    if (!this.order) return;
    this.askConfirmation('::ConfirmDelivery', '::ConfirmDeliveryMessage', '::Deliver', () => this.onConfirmDelivery());
  }

  onConfirmPayment(): void {
    if (!this.order || this.isConfirmingPayment) return;

    this.isConfirmingPayment = true;
    // Los errores los muestra el manejador global de ABP (modal con el mensaje del backend).
    this.orderService.confirmBankPayment(this.order.id)
      .pipe(finalize(() => (this.isConfirmingPayment = false)))
      .subscribe({
      next: (updatedOrder) => {
        // Update the order with the new information
        this.order.orderStatus = updatedOrder.orderStatus;
      },
      error: (error) => {
        console.error('Error confirming payment:', error);
        // TODO: Implement proper error handling, maybe show user notification
      }
    });
  }

  onConfirmDelivery(): void {
    if (!this.order) return;

    // Change order status to delivered
    this.orderService.changeOrderStatus(this.order.id, OrderStatus.Delivered).subscribe({
      next: (updatedOrder) => {
        // Update the order with the new information
        this.order.orderStatus = updatedOrder.orderStatus;
      },
      error: (error) => {
        console.error('Error delivering order:', error);
        // TODO: Implement proper error handling, maybe show user notification
      }
    });
  }

  cancelOrder(): void {
    if (!this.order) return;
    this.askConfirmation('::CancelOrder', '::ConfirmCancelOrderMessage', '::CancelOrder', () => this.onConfirmCancelOrder());
  }

  onConfirmCancelOrder(): void {
    if (!this.order) return;

    // Cancel order using the new cancelOrder method
    this.orderService.cancelOrder(this.order.id).subscribe({
      next: (updatedOrder) => {
        // Update the order with the new information
        this.order.orderStatus = updatedOrder.orderStatus;
      },
      error: (error) => {
        console.error('Error cancelling order:', error);
        // TODO: Implement proper error handling, maybe show user notification
      }
    });
  }
}
